import { useEffect } from 'react';
import { useSettingsStore } from '../stores/StoreContext';

/**
 * Custom hook to manage audio output device for all media elements
 * 
 * Automatically applies the selected audio output device from SettingsStore to:
 * - All existing <audio> and <video> elements
 * - Any new <audio> and <video> elements added to the DOM
 * 
 * When the output device changes in settings, all media elements are updated
 * to use the new device immediately.
 * 
 * Uses MutationObserver to watch for dynamically added media elements and
 * applies the output device to them automatically.
 * 
 * @returns {void}
 */
export function useAudioOutputDevice() {
    const audioOutputDevice = useSettingsStore((state) => state.audioOutputDevice);
    const audioOutputDeviceId = audioOutputDevice?.deviceId;

    useEffect(() => {
        if (!audioOutputDeviceId || typeof window === 'undefined') {
            return;
        }

        /**
         * Apply audio output device to a media element (audio or video)
         * 
         * @param {HTMLAudioElement|HTMLVideoElement} mediaElement - The media element to configure
         */
        const setOutputDevice = async (mediaElement) => {
            try {
                if (!mediaElement.setSinkId || typeof mediaElement.setSinkId !== 'function') {
                    return;
                }
                
                // Store playback state before changing device
                const wasPlaying = !mediaElement.paused;
                const currentTime = mediaElement.currentTime;
                
                await mediaElement.setSinkId(audioOutputDeviceId);
                
                // If media was playing, we need to restart it for the new sink to take effect
                // This is a browser limitation - setSinkId only affects future playback
                if (wasPlaying) {
                    try {
                        // Pause and resume to force audio to switch to new device
                        mediaElement.pause();
                        mediaElement.currentTime = currentTime;
                        await mediaElement.play();
                    } catch (err) {
                        // Silently handle playback resume errors
                    }
                }
            } catch (err) {
                // Silently handle setSinkId errors
            }
        };

        // Apply to all existing audio and video elements
        const existingMediaElements = document.querySelectorAll('audio, video');
        existingMediaElements.forEach(media => setOutputDevice(media));

        // Watch for new media elements being added to the DOM
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeName === 'AUDIO' || node.nodeName === 'VIDEO') {
                        setOutputDevice(node);
                    } else if (node.querySelectorAll) {
                        // Check if any media elements were added within this node
                        const mediaElements = node.querySelectorAll('audio, video');
                        mediaElements.forEach(media => setOutputDevice(media));
                    }
                });
            });
        });

        // Start observing the document for media element additions
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Cleanup observer on unmount or when audioOutputDeviceId changes
        return () => {
            observer.disconnect();
        };
    }, [audioOutputDeviceId]);
}

export default useAudioOutputDevice;
